import React from 'react';
import { Award, DollarSign, CheckCircle, Users } from 'lucide-react';
import { whyChooseUs } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';

const iconComponents = {
  Award,
  DollarSign,
  CheckCircle,
  Users,
};

const WhyChooseUs = () => {
  const { t } = useLanguage();
  
  return (
    <section className="py-20 bg-slate-900 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full text-sm font-semibold mb-4 border border-blue-500/30">
            {t('why.badge')}
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('why.title')}{' '}
            <span className="bg-gradient-to-r from-blue-400 to-blue-400 text-transparent bg-clip-text">
              {t('why.titleHighlight')}
            </span>
          </h2>
          <p className="text-lg text-slate-300">
            {t('why.subtitle')}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {whyChooseUs.map((feature, index) => {
            const IconComponent = iconComponents[feature.icon];
            return (
              <div
                key={feature.id}
                className="relative group"
                style={{
                  animationDelay: `${index * 150}ms`,
                }}
              >
                {/* Card */}
                <div className="relative bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover:border-blue-500 transition-all duration-300 h-full">
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-blue-500/0 group-hover:from-blue-500/10 group-hover:to-blue-500/10 rounded-2xl transition-all duration-300" />

                  {/* Content */}
                  <div className="relative z-10">
                    {/* Icon */}
                    <div className="mb-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-500 rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-12 transition-transform duration-300">
                        {IconComponent && (
                          <IconComponent className="w-7 h-7 text-white" strokeWidth={2.5} />
                        )}
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors">
                      {t(`why.${feature.icon.toLowerCase()}`)}
                    </h3>

                    {/* Description */}
                    <p className="text-slate-400 leading-relaxed">
                      {t(`why.${feature.icon.toLowerCase()}.desc`)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Stats Section */}
        <div className="mt-20 bg-gradient-to-r from-blue-500 to-blue-500 rounded-3xl p-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold text-slate-900 mb-2">500+</div>
              <div className="text-slate-800 font-semibold">{t('hero.projectsCompleted')}</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-slate-900 mb-2">100%</div>
              <div className="text-slate-800 font-semibold">{t('hero.satisfaction')}</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-slate-900 mb-2">15+</div>
              <div className="text-slate-800 font-semibold">{t('hero.yearsExperience')}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
